import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { aggridHeaderClass, AggridSwitchButton, aggridSwitchButtonWrapperStyle } from "src/components/common/common";

import useUiuxStore from "src/store/useUiuxStore";
import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags  } from "src/utils/grid/gridStyleUtils";
import { formatDate, formatDateMonth, formatDateYear4 } from "src/utils/string/stringUtil";

import { GridMonthCalendar } from "src/components/common/form/GridMonthCalendar";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { createCalendarCellClickHandler, CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, postSortHandler, useCellChangeHandler, useRowSelectionHandler, getSwitchButtonColumnOptions, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter, } from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultMST1110_R01Dto, MST1110_R01Dto } from "./Dto_MST1110_R01";
import { useMST1110_R01Store, useMST1110_R01Store_GridCalendar } from "./Store_MST1110_R01";
import { formatYear } from "react-calendar/dist/shared/dateFormatter";
import { changeWarehouseColumn, CommonCodeColumn, getCommonCodeColumnOptions, getWarehouseColumnOptions, WarehouseColumn } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";

/**
 * 사용자 관리 테이블 컴포넌트
 * 사용자 정보를 표시하고 관리하는 테이블
 */

const COLUMN_STATE_KEY = 'MST1110_R01-grid-column-state';

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<MST1110_R01Dto> | null>;
	defaultMST1110_R01Row: MST1110_R01Dto;
	handleInsertClick: (e: React.MouseEvent, rowIndex?: number) => void;
}

export const MST1110_R01Table = (props: Props) => {

	const { 
		clickable, setClickable,
		gridRef,
		defaultMST1110_R01Row,
		handleInsertClick,
	} = props;

    const { 
		MST1110_R01List, setMST1110_R01List,
		removeMST1110_R01List,
		updateMST1110_R01List,
		deletedMST1110_R01List, removeDeletedMST1110_R01List, addDeletedMST1110_R01List,
		selectedMST1110_R01List, setSelectedMST1110_R01List, removeSelectedMST1110_R01List, addSelectedMST1110_R01List, initSelectedMST1110_R01List, allSelectedMST1110_R01List,
		originalMST1110_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = useMST1110_R01Store();
	const { compCdList, unitList, wmsCdList, warehouseCdList } = useCommonCodeStore();
    const { setCodeSearchModalProps } = useUiuxStore();

	// 달력 팝업 노출 여부 상태
	const { MST1110_R01GridCalendarState, setMST1110_R01GridCalendarState, initMST1110_R01GridCalendarState } = useMST1110_R01Store_GridCalendar();
	const { pickerVisible, pickerPosition, targetRowIndex, targetField, targetDate } = MST1110_R01GridCalendarState;
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		handleSwitchButtonHandler,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: MST1110_R01List,
		defaultGridData: defaultMST1110_R01Row,
		selectedRow: selectedMST1110_R01List,
		originalGridData: originalMST1110_R01List,
		updateRow: (index: number, data: any) => updateMST1110_R01List(index, data),
	});

    const headerNames: Record<string, string> = {
		rowIndex:		"",
		locationCd:		"로케이션코드",
		locationNm:		"로케이션명",
		logicCenter:	"물류센터",
		warehouseCd:	"창고코드",
		remark:			"비고",
    }

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: [''],
		insertedDisabledCells: [''],
		commonDisabledCells: ['locationCd'],
		pointerCells: ['locationNm', 'logicCenter', 'warehouseCd', 'remark'],
		insertedPointerCells: ['locationCd'],
		commonPointerCells: [''],
		alignLeftCells: ['warehouseCd']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<MST1110_R01Dto>({
            idKey: 'valueIndex',
            selectedList: selectedMST1110_R01List,
            deletedList: deletedMST1110_R01List,
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
        selectedMST1110_R01List, deletedMST1110_R01List,
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
		createCellClassRules,
    ]);

	// 컬럼 클릭 액션 설정, 모달 이 있다면 모달 켜는 함수도 의존성 설정을 해줘야 한다
	const commonInputColumns = [ 'locationCd', 'locationNm', 'logicCenter', 'warehouseCd', 'remark', ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = [ 'locationCd', 'locationNm', 'logicCenter', 'warehouseCd', 'remark', ];

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<MST1110_R01Dto>(defaultEditableColumns),
	[defaultEditableColumns],);
	

    const handleCellChange = useCellChangeHandler({
        dataList: MST1110_R01List,
        originalDataList: originalMST1110_R01List,
        updateRow: updateMST1110_R01List,
		customAfterUpdate: (updatedData, field, value) => {
			if(field === 'logicCenter') {
				updatedData.warehouseCd = '';
			}
			return updatedData;
		},
    });

	const baseColumnDefs: ColDef<MST1110_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: MST1110_R01Dto) => renderIndexCell(
				params, 
				(innerParams: MST1110_R01Dto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedMST1110_R01List,
						selectedList: selectedMST1110_R01List,
						dataList: MST1110_R01List
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { handleInsertClick(e, rowIndex); }
					} else { 
						handleInsertClick(e, rowIndex);
					}
				},
				(rowData) => RemoveRowHandler( 
					gridRef,
					MST1110_R01List, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removeMST1110_R01List, 
					(valueIndex) => addDeletedMST1110_R01List([valueIndex.toString()])
				),
			)
		),
		{
			headerName: '로케이션코드', colId: 'locationCd', field: 'locationCd', width: 100,
			editable: (params: any) => params.data?.inserted === true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
		{
			headerName: '로케이션명', colId: 'locationNm', field: 'locationNm', width: 200,
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
		{
			headerName: '물류센터', colId: 'logicCenter', field: 'logicCenter', width: 100,
			...CommonCodeColumn.options(wmsCdList, true, CommonCodeColumn.change(updateMST1110_R01List), handleGridDropDownCommitAfter),
		},
		// {
		// 	headerName: '물류센터명', colId: 'logicCenterNm', field: 'logicCenterNm', 
		// 	editable: false
		// },
		{
			headerName: '창고코드', colId: 'warehouseCd', field: 'warehouseCd', width: 100,
			...WarehouseColumn.options(warehouseCdList, true, (params: any) => params.data?.logicCenter,
				WarehouseColumn.change(updateMST1110_R01List), handleGridDropDownCommitAfter,
			),
		},
		// {
		// 	headerName: '창고명', colId: 'warehouseNm', field: 'warehouseNm', 
		// 	editable: false
		// },
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 200,
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
		// ...IndexParameterCell(),
	], [deletedMST1110_R01List, MST1110_R01List, wmsCdList, warehouseCdList]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<MST1110_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<MST1110_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState]);


    const handleRowClick = useRowSelectionHandler({
        dataList: MST1110_R01List,
        idKey: 'valueIndex',
        selectedList: selectedMST1110_R01List,
        deletedList: deletedMST1110_R01List,
        addSelected: addSelectedMST1110_R01List,
        removeSelected: removeSelectedMST1110_R01List,
        clickable, setClickable
    });

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: MST1110_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

	const cellClickActions: CustomCellClickAction<MST1110_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => commonInputColumns.includes(field),
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], [MST1110_R01GridCalendarState, ]);
	const handleCellClick = CreateCellClickHandler(MST1110_R01List, 'valueIndex', cellClickActions);
	
	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<MST1110_R01Dto>[] = useMemo(() => [
	], []);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(MST1110_R01List, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<MST1110_R01Dto>) => {
		postSortHandler(params, MST1110_R01List, originalMST1110_R01List, setMST1110_R01List);
	}, [MST1110_R01List, originalMST1110_R01List, ]);

	const pinnedBottomRow = useMemo(() => {
		return [{
			rowIndex: '계',
			locationCd: MST1110_R01List.length.toLocaleString() + "건",
			editable: false,
		}];
	},[MST1110_R01List.length]);

    return (
		<GridContextWrapper<MST1110_R01Dto>
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setMST1110_R01List}
			initSelected={initSelectedMST1110_R01List}	allSelected={allSelectedMST1110_R01List}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
			defaultRowData={defaultMST1110_R01Row}
		>
			<Grid_Excel<MST1110_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}		
				defaultColDef={cellCssDefaultColDef}

				rowClassRules={createRowClassRules<MST1110_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedMST1110_R01List,
					deletedList: deletedMST1110_R01List
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}

				onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
                // Grid_Select에 공통 로직 처리를 위한 props 전달
                columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
				
			/>
		</GridContextWrapper>
    )
}